import { scopeHeaders } from '../lib/scopeHeaders';
import { Contact, ContactInput, ContactsResponse, Category, CategoryInput, CategoriesResponse, ImportResult } from '../types';

const API_BASE_URL = '/api';

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      Object.assign(headers as Record<string, string>, scopeHeaders());
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    tags?: string[];
    sort?: string;
    dir?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.tags?.length) searchParams.set('tags', params.tags.join(','));
    if (params?.sort) searchParams.set('sort', params.sort);
    if (params?.dir) searchParams.set('dir', params.dir);

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>('/contatos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Edição parcial (na própria linha) */
  async patchContact(id: string, data: { nome?: string; email?: string; tags?: string[]; categoriaId?: string | null }): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  /** Tags em uso no projeto, com quantos contatos têm cada uma */
  async getContactTags(): Promise<{ tags: Array<{ tag: string; count: number }> }> {
    return this.request(`/contatos/tags`);
  }

  /** Adiciona/remove tags de vários contatos sem apagar as outras */
  async bulkContactTags(contactIds: string[], add: string[], remove: string[]): Promise<{ count: number }> {
    return this.request(`/contatos/bulk/tags`, { method: 'POST', body: JSON.stringify({ contactIds, add, remove }) });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categorias/all');
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>('/categorias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      Object.assign(headers as Record<string, string>, scopeHeaders());
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      Object.assign(headers as Record<string, string>, scopeHeaders());
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  // Bulk operations
  async post(endpoint: string, data: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Chatwoot integration
  async getChatwootTags(): Promise<{ tags: Array<{ name: string; count: number }> }> {
    return this.request('/chatwoot/tags');
  }

  async syncChatwootContacts(tagMappings: Array<{ chatwootTag: string; categoryId: string }>): Promise<{ imported: number; updated: number }> {
    return this.request('/chatwoot/sync', {
      method: 'POST',
      body: JSON.stringify({ tagMappings }),
    });
  }

  // Perfex CRM Integration
  async getPerfexLeads(): Promise<{ success: boolean; leads: any[]; total: number }> {
    return this.request('/perfex/leads');
  }

  async importPerfexLeads(leadIds: string[], categoryId: string): Promise<{ success: boolean; imported: number; updated: number; errors: number }> {
    return this.request('/perfex/import', {
      method: 'POST',
      body: JSON.stringify({ leadIds, categoryId }),
    });
  }

  async getPerfexStatuses(): Promise<{ success: boolean; statuses: Array<{ id: string; name: string; color?: string }> }> {
    return this.request('/perfex/statuses');
  }

  async getPerfexSources(): Promise<{ success: boolean; sources: Array<{ id: string; name: string }> }> {
    return this.request('/perfex/sources');
  }

  async getPerfexStaff(): Promise<{ success: boolean; staff: Array<{ staffid: string; firstname: string; lastname: string; email: string }> }> {
    return this.request('/perfex/staff');
  }

  // ===== Caixa de entrada (feature de plano `inbox`) =====
  // Espelha os 12 endpoints do backend (backend/src/routes/inbox.ts, PR #201).
  // Nota: o backend expõe a ação de responder em POST /:id/messages (não /:id/reply),
  // o ajuste de status em POST /:id/status (não PATCH) e o body do /assign usa
  // `userId` (não `agentId`). As notas não têm GET dedicado — vêm em
  // `messages[]` com `kind: 'NOTE'` no retorno do `get`.
  inbox = {
    list: (filters: { view?: string; channel?: string; q?: string; cursor?: string; intent?: string; urgent?: boolean } = {}) => {
      const sp = new URLSearchParams();
      if (filters.view) sp.set('view', filters.view);
      if (filters.channel) sp.set('channel', filters.channel);
      if (filters.q) sp.set('q', filters.q);
      if (filters.cursor) sp.set('cursor', filters.cursor);
      if (filters.intent) sp.set('intent', filters.intent);
      if (filters.urgent) sp.set('urgent', '1');
      const qs = sp.toString();
      return this.request<{
        success: boolean;
        counts: { mine: number; queue: number; open: number };
        nextCursor: string | null;
        conversations: Array<{
          id: string;
          channel: 'whatsapp' | 'instagram' | 'widget';
          status: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED';
          lastMessageAt: string;
          lastPreview: string | null;
          unread: number;
          contact: { id: string; nome: string; telefone: string | null; tags: string[] } | null;
          assignee: { id: string; nome: string } | null;
          last: { content: string } | null;
          intent?: string | null;
          intentConfidence?: number | null;
          urgent?: boolean;
        }>;
      }>(`/inbox${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => this.request<{
      success: boolean;
      conversation: {
        id: string;
        channel: 'whatsapp' | 'instagram' | 'widget';
        status: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED';
        summary: string | null;
        summaryGeneratedAt: string | null;
        summaryModel: string | null;
        lastMessageAt: string;
        lastInboundAt: string | null;
      };
      contact: { id: string; nome: string; telefone: string | null; email: string | null; tags: string[] } | null;
      assignee: { id: string; nome: string } | null;
      window: { open: boolean; closesAt: string | null };
      messages: Array<{
        id: string;
        kind: 'INBOUND' | 'OUTBOUND' | 'NOTE' | 'SYSTEM' | 'AI';
        direction: 'IN' | 'OUT';
        content: string;
        mediaType: string | null;
        mediaUrl: string | null;
        authorId: string | null;
        author: string | null;
        createdAt: string;
      }>;
    }>(`/inbox/${id}`),
    reply: (id: string, text: string) => this.request<{ success: boolean; message: { id: string; createdAt: string } }>(`/inbox/${id}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),
    claim: (id: string) => this.request<{ success: boolean; conversation: { id: string; assignedUserId: string | null } }>(`/inbox/${id}/claim`, { method: 'POST' }),
    assign: (id: string, userId: string | null) => this.request<{ success: boolean; conversation: { id: string; assignedUserId: string | null } }>(`/inbox/${id}/assign`, { method: 'POST', body: JSON.stringify({ userId }) }),
    setStatus: (id: string, status: 'OPEN' | 'AI' | 'HUMAN' | 'CLOSED') => this.request<{ success: boolean; conversation: { id: string; status: string } }>(`/inbox/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
    addNote: (id: string, text: string) => this.request<{ success: boolean; message: { id: string; createdAt: string } }>(`/inbox/${id}/notes`, { method: 'POST', body: JSON.stringify({ text }) }),
    suggest: (id: string) => this.request<{ success: boolean; text: string }>(`/inbox/${id}/suggest`, { method: 'POST' }),
    summary: (id: string) => this.request<{ success: boolean; summary: string; model: string | null }>(`/inbox/${id}/summary`, { method: 'POST' }),
    agents: () => this.request<{ success: boolean; agents: Array<{ id: string; nome: string }> }>(`/inbox/agents`),
    /** Token curto (5 min) para o WebSocket da Caixa no Durable Object InboxRoom */
    wsToken: () => this.request<{ success: boolean; token: string; tenantId: string; expiresAt: string; url: string | null }>(`/inbox/ws-token`),
  };
  // ============================================================
  // Funil de vendas (P2 #159) — backend em backend/src/routes/funnel.ts
  // Endpoints consumidos:
  //   GET    /api/funnel/pipelines                 — listar pipelines
  //   GET    /api/funnel/deals?...                 — listagem paginada de negócios
  //   POST   /api/funnel/deals                     — criar negócio
  //   GET    /api/funnel/deals/:id                 — detalhe
  //   PATCH  /api/funnel/deals/:id                 — atualizar campos editáveis
  //   POST   /api/funnel/deals/:id/stage           — mover de coluna (Kanban DnD)
  //   GET    /api/funnel/deals/:id/tasks           — tarefas do negócio
  //   POST   /api/funnel/deals/:id/tasks           — criar tarefa
  //   PATCH  /api/funnel/tasks/:id                 — atualizar tarefa (done, etc.)
  //   DELETE /api/funnel/tasks/:id                 — remover tarefa
  //   GET    /api/funnel/deals/:id/activities      — histórico/linha do tempo
  //   POST   /api/funnel/deals/:id/activities      — adicionar nota/mensagem
  // ============================================================
  async listFunnelPipelines(projectId?: string | null): Promise<{ success: boolean; pipelines: any[]; defaultPipelineId: string | null }> {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return this.request(`/funnel/pipelines${qs}`);
  }

  async listFunnelDeals(filters: {
    projectId?: string | null;
    pipelineId?: string | null;
    stageId?: string | null;
    ownerId?: string | null;
    status?: string | null;
    search?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ success: boolean; deals: any[]; total: number; page: number; pageSize: number }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    return this.request(`/funnel/deals${qs ? `?${qs}` : ''}`);
  }

  async getFunnelDeal(id: string): Promise<{ success: boolean; deal: any }> {
    return this.request(`/funnel/deals/${id}`);
  }

  async createFunnelDeal(data: {
    title: string;
    pipelineId?: string | null;
    stageId?: string | null;
    contactId?: string | null;
    ownerId?: string | null;
    amount?: number | null;
    currency?: string;
    expectedCloseAt?: string | null;
    origin?: string | null;
    customFields?: Record<string, unknown> | null;
  }): Promise<{ success: boolean; deal: any }> {
    return this.request('/funnel/deals', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateFunnelDeal(id: string, data: Partial<{
    title: string;
    amount: number | null;
    currency: string;
    expectedCloseAt: string | null;
    ownerId: string | null;
    customFields: Record<string, unknown> | null;
    lostReason: string | null;
  }>): Promise<{ success: boolean; deal: any }> {
    return this.request(`/funnel/deals/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async moveFunnelDeal(id: string, stageId: string, position?: number): Promise<{ success: boolean; deal: any; activityId: string }> {
    return this.request(`/funnel/deals/${id}/stage`, {
      method: 'POST',
      body: JSON.stringify({ stageId, position }),
    });
  }

  async listFunnelDealTasks(dealId: string): Promise<{ success: boolean; tasks: any[] }> {
    return this.request(`/funnel/deals/${dealId}/tasks`);
  }

  async createFunnelDealTask(dealId: string, data: { title: string; description?: string | null; dueAt?: string | null; ownerId?: string | null }): Promise<{ success: boolean; task: any }> {
    return this.request(`/funnel/deals/${dealId}/tasks`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateFunnelTask(taskId: string, data: Partial<{ title: string; description: string | null; dueAt: string | null; doneAt: string | null }>): Promise<{ success: boolean; task: any }> {
    return this.request(`/funnel/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteFunnelTask(taskId: string): Promise<{ success: boolean }> {
    return this.request(`/funnel/tasks/${taskId}`, { method: 'DELETE' });
  }

  async listFunnelDealActivities(dealId: string, limit = 100): Promise<{ success: boolean; activities: any[] }> {
    return this.request(`/funnel/deals/${dealId}/activities?limit=${limit}`);
  }

  async addFunnelDealActivity(dealId: string, kind: 'note' | 'message' | 'task', payload: Record<string, unknown> = {}): Promise<{ success: boolean; activity: any }> {
    return this.request(`/funnel/deals/${dealId}/activities`, { method: 'POST', body: JSON.stringify({ kind, payload }) });
  }
}

export const apiService = new ApiService();
export const inboxApi = apiService.inbox;
export const api = apiService;